import React, { useCallback, useMemo } from 'react';
import { useAsyncValue, useRevalidator } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { DocumentNode } from '@apollo/client';

// Components
import { AddExternalCompanyForm } from '../../../../components/forms/AddExternalCompanyForm';
import { ExternalCompaniesManagementTable } from '../../../../components/display/tables/formTables/managementTables/ExternalCompaniesManagementTable';

// Constants
import { formExternalCompanyManagementNaming } from '../../../../components/forms/AddExternalCompanyForm/constants';

// Types
import { ExternalCompaniesManagementLoaderData } from './types';
import { ExternalCompanyManagement } from '../../../../components/forms/AddExternalCompanyForm/types';
import { ExternalCompanyRow } from '../../../../components/display/tables/formTables/managementTables/ExternalCompaniesManagementTable/types';

// Redux Actions
import { openModal } from '../../../../state/actions/ui/modal';
import { modalIds } from '../../../../components/modals/SuperModal/types';

// Styles
import { Container, ExternalCompanyManagementSection } from './styles';

// GQL
import { client } from '../../../../gql/client';
import {
  ACTIVATE_EXTERNAL_COMPANY,
  CREATE_EXTERNAL_COMPANY,
  DEACTIVATE_EXTERNAL_COMPANY,
  DELETE_EXTERNAL_COMPANY,
} from '../../../../gql/mutations/externalCompanies';

// Utils
import { showBackendErrorToast } from '../../../../utils/toasts';

/**
 * Misma normalización que el backend (`normalizeCompanyName`): minúsculas y
 * sin espacios. Se replica aquí para avisar del duplicado ANTES de ir al
 * servidor; el backend la repite como backstop porque el catálogo puede haber
 * cambiado desde que se cargó la pantalla.
 */
const normalizeCompanyName = (name: string): string =>
  name.trim().toLowerCase().replace(/\s+/g, '');

export default function ExternalCompaniesManagementPage(): JSX.Element {
  const loaderData = useAsyncValue() as ExternalCompaniesManagementLoaderData;
  const revalidator = useRevalidator();
  const dispatch = useDispatch();
  const { t } = useTranslation(['pages']);

  const allCompanies = useMemo(
    () => [...loaderData.activeCompanies, ...loaderData.inactiveCompanies],
    [loaderData.activeCompanies, loaderData.inactiveCompanies]
  );

  const onReFetchData = useCallback(() => {
    revalidator.revalidate();
  }, [revalidator]);

  const onFormSubmit = async (data: ExternalCompanyManagement) => {
    const target = normalizeCompanyName(data.companyName);
    const alreadyExists = allCompanies.some(
      (company) => normalizeCompanyName(company.companyName) === target
    );

    if (alreadyExists) {
      return t('pages:management.errors.duplicateCompany');
    }

    try {
      await client.mutate({
        mutation: CREATE_EXTERNAL_COMPANY,
        variables: { companyName: data.companyName.trim() },
        refetchQueries: ['GetExternalCompanies'],
        awaitRefetchQueries: true,
      });
      onReFetchData();
      return undefined;
    } catch (error) {
      // El form pinta el mensaje bajo el campo, que es donde el usuario está
      // mirando; el toast sobraría aquí.
      return t('pages:management.errors.duplicateCompany');
    }
  };

  /**
   * Acciones de fila. El error va por toast siguiendo la doctrina del
   * proyecto: `showBackendErrorToast` traduce el código estable del backend
   * (EXTERNAL_COMPANY_IN_USE, EXTERNAL_COMPANY_GLOBAL_READONLY...) vía
   * `errors:backend.<code>`, y cae a un genérico si es un error técnico.
   */
  const runMutation = useCallback(
    async (mutation: DocumentNode, variables: Record<string, unknown>) => {
      try {
        await client.mutate({
          mutation,
          variables,
          refetchQueries: ['GetExternalCompanies'],
          awaitRefetchQueries: true,
        });
        onReFetchData();
      } catch (error) {
        showBackendErrorToast(
          error,
          t('pages:management.errors.companyGeneric')
        );
      }
    },
    [onReFetchData, t]
  );

  /**
   * Renombrar y eliminar van por SuperModal (mismo patrón que Equipos): la
   * tabla solo pide la acción, el modal la confirma o la ejecuta. Antes el
   * renombrado usaba `window.prompt` y el borrado duro no se confirmaba —
   * inaceptable para una acción irreversible.
   */
  const onEditCompany = useCallback(
    (company: ExternalCompanyRow) => {
      dispatch(
        openModal(modalIds.EDIT_EXTERNAL_COMPANY_MODAL, {
          company: { id: company.id, companyName: company.companyName },
          existingCompanies: allCompanies.map((item) => ({
            id: item.id,
            companyName: item.companyName,
          })),
          onSuccess: onReFetchData,
        })
      );
    },
    [dispatch, allCompanies, onReFetchData]
  );

  const onDeleteCompany = useCallback(
    (company: ExternalCompanyRow) => {
      dispatch(
        openModal(modalIds.DELETE_EXTERNAL_COMPANY_MODAL, {
          companyName: company.companyName,
          onConfirmDelete: async () => {
            await runMutation(DELETE_EXTERNAL_COMPANY, { id: company.id });
          },
        })
      );
    },
    [dispatch, runMutation]
  );

  return (
    <Container>
      <ExternalCompanyManagementSection>
        <AddExternalCompanyForm
          initialData={loaderData.cacheData}
          onFormSubmit={onFormSubmit}
          namingPersistForm={
            formExternalCompanyManagementNaming.NEW_EXTERNAL_COMPANY_MANAGEMENT_FORM
          }
        />
      </ExternalCompanyManagementSection>

      <ExternalCompanyManagementSection>
        <ExternalCompaniesManagementTable
          data={{
            activeCompanies: loaderData.activeCompanies,
            inactiveCompanies: loaderData.inactiveCompanies,
          }}
          onDeactivate={(id) =>
            runMutation(DEACTIVATE_EXTERNAL_COMPANY, { id })
          }
          onActivate={(id) => runMutation(ACTIVATE_EXTERNAL_COMPANY, { id })}
          onEditCompany={onEditCompany}
          onDeleteCompany={onDeleteCompany}
        />
      </ExternalCompanyManagementSection>
    </Container>
  );
}
